import { useState, useEffect, useRef } from 'react'

import { Box, Button, Card, Icon, Modal, TextArea } from 'components'
import Tooltip from 'components/atomic/Tooltip/Tooltip'
import { BaseEmoji, Picker } from 'components/EmojiPicker'
import { toast } from 'react-toastify'

import { useApp } from 'store/app/hooks'
import { useAuth } from 'store/auth/hooks'
import { useNews } from 'store/news/hooks'
import { countWords, validateWordLimit, formatWordCount } from 'utils/wordCounter'
import { useTokenGating } from 'hooks/useTokenGating'
import { track } from 'utils/analytics'
import { GifPickerModal } from 'components/Modals/GifPickerModal'

import DefaultAvatar from 'assets/avatar.webp' // Make sure DefaultAvatar is imported

export const PostModal = () => {
  const { user } = useAuth()
  const { isPostModalOpen, setIsPostModalOpen } = useApp()
  const { isLoading, postNews, createNews } = useNews()
  const { hasExtendedWords } = useTokenGating()

  const [text, setText] = useState<string>('')
  const [uploadImg, setUploadImg] = useState<File>()
  const [gifUrl, setGifUrl] = useState<string>('')
  const [showGifPicker, setShowGifPicker] = useState<boolean>(false)
  const [showEmojis, setShowEmojis] = useState<boolean>(false)
  const [wordCount, setWordCount] = useState<number>(0)
  const [isOverLimit, setIsOverLimit] = useState<boolean>(false)

  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleSelectGif = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const file = new File([blob], 'gif-upload.gif', { type: 'image/gif' })
      setUploadImg(file)
      setGifUrl(url)
    } catch (error) {
      console.error('Error loading GIF:', error)
      toast.error('Failed to load GIF')
    }
  }

  const handleEmojiPickup = (emoji: BaseEmoji) => {
    if (inputRef.current) {
      const cursorPosition = inputRef.current?.selectionStart || 0
      // concat the emoji to the string
      const value =
        text.slice(0, cursorPosition) +
        emoji.native +
        text.slice(cursorPosition)
      
      // Check word count before setting
      const validation = validateWordLimit(value, 100)
      if (!validation.isValid) {
        toast.error(`Cannot add more text. ${validation.message}`, {
          position: 'top-center',
          autoClose: 3000,
        })
        return
      }
      
      setText(value)
      const newCursorPosition = cursorPosition + emoji.native.length
      // allow to add multiple emojis in the same position of string
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(
            newCursorPosition,
            newCursorPosition,
          )
        }
      }, 10)
    }
  }

  const handleSubmit = () => {
    if (!text.trim()) return // Don't submit if text is empty
    
    // Validate word count before submission
    const validation = validateWordLimit(text, 100)
    if (!validation.isValid) {
      toast.error(`Cannot post. ${validation.message}`, {
        position: 'top-center',
        autoClose: 5000,
      })
      return
    }
    
    createNews({ title: 'default title', image: uploadImg, body: text })
    track('post_published', { word_count: countWords(text) })
  }

  // Monitor word count (without showing automatic toasts)
  useEffect(() => {
    const count = countWords(text)
    setWordCount(count)
    setIsOverLimit(count > 100)
  }, [text])

  // Close modal and reset form after successful post
  useEffect(() => {
    if (!isLoading && postNews && postNews.news) {
      setIsPostModalOpen(false)
      // Reset form
      setText('')
      setUploadImg(undefined)
      setGifUrl('')
      setShowEmojis(false)
      setWordCount(0)
      setIsOverLimit(false)
    }
  }, [isLoading, postNews, setIsPostModalOpen])

  const handleClose = () => {
    setIsPostModalOpen(false)
    setText('')
    setUploadImg(undefined)
    setGifUrl('')
    setShowEmojis(false)
    setWordCount(0)
    setIsOverLimit(false)
  }

  // If user isn't loaded yet, don't render the modal content
  const renderModalContent = () => {
    // Return early if user is not available yet
    if (!user) {
      return (
        <Card className="flex items-center justify-center !px-6 !py-2" stretch size="lg">
          <p>Loading user information...</p>
        </Card>
      );
    }

    return (
      <Card
        className="flex items-center justify-center !px-6 !py-2"
        stretch
        size="lg"
      >
        <Box className="space-x-4">
          <img
            className="w-12 h-12 rounded-full"
            src={user.avatar?.url || DefaultAvatar}
            alt="Your avatar"
            onError={(e) => {
              // Handle image load error
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = DefaultAvatar;
            }}
          />
          <Box col className="w-full relative">
            {!hasExtendedWords && (
              <Tooltip text="Hold 1 LEET token to unlock 1000 word limit">
                <div className="absolute top-0 right-0 cursor-help z-10">
                  <Icon name="lockFill" size={14} color="red" />
                </div>
              </Tooltip>
            )}
            <div className="relative bg-[#1a1a1a] border border-gray-700 rounded-lg focus-within:border-[#02f305] transition-colors overflow-hidden">
              <textarea
                ref={inputRef}
                rows={6}
                autoFocus
                placeholder="What's happening? (100 words max)"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className={`w-full px-4 py-3 pb-10 bg-transparent text-white resize-none focus:outline-none placeholder-gray-500 ${
                  isOverLimit ? 'text-red-400' : ''
                }`}
                style={{
                  minHeight: '120px',
                  maxHeight: '400px',
                  lineHeight: '1.6',
                  fontSize: '15px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word'
                }}
              />
              {/* Bottom bar with word count and formatting hints */}
              <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-[#0f0f0f] border-t border-gray-800 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="hidden sm:inline">Tip: Use Enter for line breaks</span>
                </div>
                <div className={`text-xs font-semibold px-2 py-1 rounded ${
                  isOverLimit ? 'bg-red-500/20 text-red-400' :
                  wordCount > 90 ? 'bg-yellow-500/20 text-yellow-400' :
                  wordCount > 75 ? 'bg-orange-500/20 text-orange-400' :
                  'bg-gray-700/50 text-gray-400'
                }`}>
                  {formatWordCount(wordCount, 100)}
                </div>
              </div>
            </div>
            {uploadImg && (
              <Box className="relative my-2 rounded-lg overflow-hidden border border-dark-border">
                <div
                  className="absolute top-1 left-1 w-6 h-6 rounded-full bg-btn-dark-tint flex justify-center items-center cursor-pointer z-10"
                  onClick={() => {
                    setUploadImg(undefined)
                    setGifUrl('')
                  }}
                >
                  <Icon name="close" size={16} color="white" />
                </div>
                <img
                  className="w-full min-h-[284px] max-h-[500px] object-cover"
                  src={gifUrl || URL.createObjectURL(uploadImg)}
                  alt="Upload preview"
                />
              </Box>
            )}
            <input
              accept="image/*"
              className="hidden"
              id="img-upload-modal"
              type="file"
              onChange={(e) => {
                const fileList = e.target.files
                if (!fileList || fileList.length === 0) return
                setUploadImg(fileList[0])
              }}
            />
            <Box alignCenter justify="between">
              <Box col>
                <Box>
                  <Button
                    size="xs"
                    type="borderless"
                    startIcon={
                      <label htmlFor="img-upload-modal">
                        <Icon name="image" size={16} color="blue" />
                      </label>
                    }
                  />
                  <Button
                    size="xs"
                    type="borderless"
                    onClick={() => setShowGifPicker(true)}
                    startIcon={<Icon name="gif" size={16} color="blue" />}
                  />
                  <Button
                    size="xs"
                    type="borderless"
                    startIcon={<Icon name="graph" size={16} color="blue" />}
                  />
                  <Button
                    size="xs"
                    type="borderless"
                    startIcon={<Icon name="smile" size={16} color="blue" />}
                    onClick={() => setShowEmojis(!showEmojis)}
                  />
                  <Button
                    size="xs"
                    type="borderless"
                    startIcon={
                      <Icon name="calendar" size={16} color="blue" />
                    }
                  />
                </Box>
              </Box>
              <Button
                loading={isLoading}
                className={`w-20 ${isOverLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
                isFancy
                onClick={handleSubmit}
                disabled={!text.trim() || isOverLimit} // Disable if no text or over limit
                title={isOverLimit ? 'Reduce your shout to 100 words or less' : ''}
              >
                Shout
              </Button>
            </Box>
            {showEmojis && (
              <Picker
                theme="dark"
                title="Pick your emoji"
                emoji="point_up"
                onClick={() => inputRef.current?.focus()}
                onSelect={handleEmojiPickup}
              />
            )}
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <>
      <Modal
        title="Write your post here"
        isOpened={isPostModalOpen}
        onClose={handleClose}
        withCard={false}
      >
        {renderModalContent()}
      </Modal>

      <GifPickerModal
        isOpen={showGifPicker}
        onClose={() => setShowGifPicker(false)}
        onSelectGif={handleSelectGif}
      />
    </>
  )
}